import React, { useMemo } from 'react';
import { Bell } from 'lucide-react';
import {
  LST_CARGO_LABELS,
  type LstCargo,
  type LstExecucao,
  type LstFrequency,
  type LstStatus,
  type LstTemplate,
} from '../types';
import {
  buildHourlyGrid,
  buildShiftHourSlots,
  formatTimeHHmm,
  getHourlyTemplates,
  hourBlockHasUpcomingAlert,
  shortenActivityTitle,
  type HourActivityCardState,
  type HourCellActivity,
  type HourCellData,
} from '../services/lstMatrixHourly';
import { isTemplateDueOnDate } from '../services/lstSchedule';
import { isLstSlotStillOpen } from '../utils/lstEffectiveStatus';
import { KamishibaiMatrixCell, KamishibaiSymbolBadge } from './kamishibai/KamishibaiSymbol';
import { KAMISHIBAI_NA_CELL_BG } from '../utils/kamishibaiStatus';
import { KamishibaiMatrixSubHeader } from './kamishibai/KamishibaiMatrixSubHeader';
import { KamishibaiLegendBar } from './kamishibai/KamishibaiLegendBar';
import { useKamishibaiMatrixModel } from '../hooks/useKamishibaiMatrixModel';
import type { LstPeriodView } from './PeriodSwitcher';
import type { LeaderSelectOption } from './MeuPadraoSubHeader';

/** Bloco de frequência da matriz (traduz a planilha física). */
type LstBlock = 'diaria' | 'semanal' | 'mensal';

interface LstMatrixMensalProps {
  cargo: LstCargo;
  templates: LstTemplate[];
  execucoes: LstExecucao[];
  monthDates: string[];
  userId: string;
  title: string;
  subtitle: string;
  onCellClick: (template: LstTemplate, date: string, execucao: LstExecucao | null, code: string) => void;
  onHourBlockClick?: (cell: HourCellData) => void;
  /** Layout compacto unificado — sub-header + grade + legenda sticky (Meu Padrão). */
  unifiedChrome?: boolean;
  periodView?: LstPeriodView;
  onPeriodChange?: (view: LstPeriodView) => void;
  chromeTitle?: string;
  chromeContextLine?: string;
  leaderOptions?: LeaderSelectOption[];
  selectedLeaderId?: string;
  onLeaderChange?: (userId: string) => void;
  viewingSubordinate?: boolean;
}

const WEEKDAY_SHORT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function blockOf(freq: LstFrequency): LstBlock {
  if (freq === 'semanal') return 'semanal';
  if (freq === 'mensal') return 'mensal';
  return 'diaria';
}

const BLOCK_META: Record<
  LstBlock,
  { label: string; prefix: string; head: string; code: string; section: string }
> = {
  diaria: {
    label: 'Frequência Diária',
    prefix: 'D',
    head: 'bg-amber-50/70 dark:bg-amber-900/15',
    code: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    section: 'bg-amber-50/60 text-amber-700 dark:bg-amber-900/10 dark:text-amber-300',
  },
  semanal: {
    label: 'Frequência Semanal',
    prefix: 'S',
    head: 'bg-blue-50/70 dark:bg-blue-900/15',
    code: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    section: 'bg-blue-50/60 text-blue-700 dark:bg-blue-900/10 dark:text-blue-300',
  },
  mensal: {
    label: 'Frequência Mensal',
    prefix: 'M',
    head: 'bg-emerald-50/70 dark:bg-emerald-900/15',
    code: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    section: 'bg-emerald-50/60 text-emerald-700 dark:bg-emerald-900/10 dark:text-emerald-300',
  },
};

const BLOCK_ORDER: LstBlock[] = ['diaria', 'semanal', 'mensal'];

function dayInfo(iso: string): { num: number; weekday: string; isWeekend: boolean } {
  const [y, m, d] = iso.split('-').map(Number);
  const dow = new Date(y, m - 1, d).getDay();
  return { num: d, weekday: WEEKDAY_SHORT[dow], isWeekend: dow === 0 || dow === 6 };
}

function execToMatrixStatus(
  exec: LstExecucao | null,
  stillOpen: boolean,
): LstStatus | 'pendente' {
  if (exec?.status) return exec.status;
  if (stillOpen) return 'pendente';
  return 'nao_realizado';
}

function nowMinutes(): number {
  const n = new Date();
  return n.getHours() * 60 + n.getMinutes();
}

/**
 * Matriz Mensal do Trabalho Padronizado (LST).
 * Visão por Atividades (D/S/M × dias) ou Visão por Horário do Turno (hora × dias).
 */
export const LstMatrixMensal: React.FC<LstMatrixMensalProps> = ({
  cargo,
  templates,
  execucoes,
  monthDates,
  userId,
  title,
  subtitle,
  onCellClick,
  onHourBlockClick,
  unifiedChrome = false,
  periodView = 'semana',
  onPeriodChange,
  chromeTitle,
  chromeContextLine,
  leaderOptions,
  selectedLeaderId,
  onLeaderChange,
  viewingSubordinate = false,
}) => {
  const model = useKamishibaiMatrixModel({
    cargo,
    templates,
    execucoes,
    monthDates,
    userId,
  });

  const {
    today,
    viewMode,
    setViewMode,
    blocks,
    days,
    execIndex,
    hourSlots,
    hourlyGrid,
    activityStats,
    hourlyStats,
    totalRotinas,
    hasHourlyData,
  } = model;

  const gridBody =
    viewMode === 'activities' ? (
      totalRotinas === 0 ? (
        <EmptyState cargo={cargo} flush={unifiedChrome} />
      ) : (
        <ActivitiesGrid
          blocks={blocks}
          days={days}
          today={today}
          monthDates={monthDates}
          execIndex={execIndex}
          onCellClick={onCellClick}
          flush={unifiedChrome}
        />
      )
    ) : !hasHourlyData ? (
      <div className="flex h-full items-center justify-center text-[12px] text-slate-400">
        Nenhuma rotina cadastrada para mapear na grade horária.
      </div>
    ) : (
      <HourlyGrid
        hourSlots={hourSlots}
        days={days}
        today={today}
        hourlyGrid={hourlyGrid}
        onCellClick={onCellClick}
        flush={unifiedChrome}
      />
    );

  if (unifiedChrome && onPeriodChange) {
    const stats = viewMode === 'activities' ? activityStats : hourlyStats;
    return (
      <div className="flex h-full min-h-0 flex-col">
        <KamishibaiMatrixSubHeader
          periodView={periodView}
          onPeriodChange={onPeriodChange}
          title={chromeTitle ?? title}
          contextLine={chromeContextLine ?? subtitle}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          stats={stats}
          statsMode={viewMode}
          leaderOptions={leaderOptions}
          selectedLeaderId={selectedLeaderId}
          onLeaderChange={onLeaderChange}
          viewingSubordinate={viewingSubordinate}
        />
        <div className="min-h-0 flex-1 overflow-hidden">{gridBody}</div>
        <KamishibaiLegendBar viewMode={viewMode} />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-col pb-6">
      <div className="flex shrink-0 flex-wrap items-end justify-between gap-3 px-1 pb-3">
        <div>
          <h2 className="text-[15px] font-bold text-slate-800 dark:text-slate-100">{title}</h2>
          <p className="text-[11px] text-slate-500 dark:text-slate-400">{subtitle}</p>
        </div>
        {viewMode === 'activities' ? (
          <div className="flex items-center gap-4">
            <SummaryPill label="Realizadas" value={activityStats.ok} tone="emerald" />
            <SummaryPill label="Com desvio" value={activityStats.desvio} tone="amber" />
            <SummaryPill label="Perdas" value={activityStats.perdido} tone="red" />
            <div className="flex flex-col items-end">
              <span className="text-[18px] font-bold tabular-nums text-slate-800 dark:text-slate-100">
                {activityStats.pct}%
              </span>
              <span className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">
                Aderência
              </span>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-4">
            <SummaryPill label="Realizadas" value={hourlyStats.ok} tone="emerald" />
            <SummaryPill label="Com desvio" value={hourlyStats.desvio} tone="amber" />
            <SummaryPill label="Perdas" value={hourlyStats.perdido} tone="red" />
            {(hourlyStats.alertBlocks ?? 0) > 0 && (
              <SummaryPill label="Alertas hoje" value={hourlyStats.alertBlocks!} tone="amber" />
            )}
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center justify-between gap-3 px-1 pb-2">
        <div className="inline-flex overflow-hidden rounded-md border border-slate-200 bg-slate-50/80 p-0.5 dark:border-slate-700 dark:bg-slate-900/50">
          <ViewToggleButton
            active={viewMode === 'activities'}
            onClick={() => setViewMode('activities')}
            label="Visão por Atividades"
          />
          <ViewToggleButton
            active={viewMode === 'hourly'}
            onClick={() => setViewMode('hourly')}
            label="Visão por Horário do Turno"
          />
        </div>
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-1 px-1 pb-2 text-[10px] text-slate-500 dark:text-slate-400">
        <LegendItem icon={<KamishibaiSymbolBadge symbol="O" size="sm" />} label="Conforme (O)" />
        <LegendItem icon={<KamishibaiSymbolBadge symbol="triangle" size="sm" />} label="Desvio (Δ)" />
        <LegendItem icon={<KamishibaiSymbolBadge symbol="X" size="sm" />} label="Não realizado (X)" />
        {viewMode === 'hourly' && (
          <LegendItem icon={<Bell size={10} className="text-amber-500" />} label="Alerta antecipado (hoje)" />
        )}
      </div>

      {gridBody}
    </div>
  );
};

// ─── Visão por Atividades ───────────────────────────────────

const ActivitiesGrid: React.FC<{
  blocks: { block: LstBlock; items: { template: LstTemplate; code: string }[] }[];
  days: { iso: string; num: number; weekday: string; isWeekend: boolean }[];
  today: string;
  monthDates: string[];
  execIndex: Map<string, LstExecucao>;
  onCellClick: LstMatrixMensalProps['onCellClick'];
  flush?: boolean;
}> = ({ blocks, days, today, monthDates, execIndex, onCellClick, flush = false }) => (
  <div
    className={
      flush
        ? 'h-full min-h-0 flex-1 overflow-auto'
        : 'min-h-0 flex-1 overflow-auto rounded-lg border border-slate-200 dark:border-slate-800'
    }
  >
    <table className="w-full border-collapse text-[11px]">
      <thead className="sticky top-0 z-20">
        <tr className="bg-slate-50 dark:bg-slate-900">
          <th className="sticky left-0 z-30 min-w-[220px] border-b border-r border-slate-200 bg-slate-50 px-3 py-1.5 text-left text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">
            Atividade
          </th>
          {days.map((day) => (
            <DayHeader key={day.iso} day={day} today={today} />
          ))}
        </tr>
      </thead>
      <tbody>
        {blocks.map((grp) => (
          <React.Fragment key={grp.block}>
            <tr>
              <td
                colSpan={days.length + 1}
                className={`border-b border-slate-200 px-3 py-1 text-[9px] font-bold uppercase tracking-[0.08em] dark:border-slate-800 ${BLOCK_META[grp.block].section}`}
              >
                {BLOCK_META[grp.block].label}
              </td>
            </tr>
            {grp.items.map(({ template, code }) => (
              <tr key={template.id} className="group/row">
                <th
                  scope="row"
                  className="sticky left-0 z-10 min-w-[220px] border-b border-r border-slate-200 bg-white px-2 py-1.5 text-left align-middle font-normal transition-colors group-hover/row:bg-slate-50 dark:border-slate-800 dark:bg-slate-950 dark:group-hover/row:bg-slate-900"
                >
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex h-5 min-w-[26px] shrink-0 items-center justify-center rounded px-1 text-[9px] font-bold ${BLOCK_META[grp.block].code}`}>
                      {code}
                    </span>
                    <span className="flex min-w-0 items-center gap-1">
                      {template.is_critical && (
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-red-500" title="Rotina crítica" />
                      )}
                      <span className="truncate text-[11.5px] font-medium text-slate-700 dark:text-slate-200" title={template.title}>
                        {template.title}
                      </span>
                      {template.scheduled_time && (
                        <span className="ml-1 shrink-0 text-[9px] tabular-nums text-slate-400">
                          {formatTimeHHmm(template.scheduled_time) ?? template.scheduled_time}
                        </span>
                      )}
                    </span>
                  </div>
                </th>
                {days.map((day) => {
                  if (!isTemplateDueOnDate(template, day.iso, monthDates)) {
                    return (
                      <InactiveDayCell
                        key={day.iso}
                        isWeekend={day.isWeekend}
                        isToday={day.iso === today}
                      />
                    );
                  }
                  const exec = execIndex.get(`${template.id}__${day.iso}`) ?? null;
                  const matrixStatus = execToMatrixStatus(
                    exec,
                    isLstSlotStillOpen(template.scheduled_time, day.iso, today),
                  );
                  return (
                    <ActivityCell
                      key={day.iso}
                      status={matrixStatus}
                      isWeekend={day.isWeekend}
                      isToday={day.iso === today}
                      onClick={() => onCellClick(template, day.iso, exec, code)}
                      label={`${code} · dia ${day.num}`}
                    />
                  );
                })}
              </tr>
            ))}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  </div>
);

// ─── Visão por Horário do Turno ─────────────────────────────

const HourlyGrid: React.FC<{
  hourSlots: { hour: number; label: string }[];
  days: { iso: string; num: number; weekday: string; isWeekend: boolean }[];
  today: string;
  hourlyGrid: Map<string, HourCellData>;
  onCellClick: LstMatrixMensalProps['onCellClick'];
  flush?: boolean;
}> = ({ hourSlots, days, today, hourlyGrid, onCellClick, flush = false }) => {
  const nowMin = nowMinutes();

  const maxCardsPerHour = useMemo(() => {
    const counts = new Map<number, number>();
    for (const slot of hourSlots) {
      let max = 0;
      for (const day of days) {
        const cell = hourlyGrid.get(`${day.iso}__${slot.hour}`);
        if (cell) max = Math.max(max, cell.activities.length);
      }
      counts.set(slot.hour, max);
    }
    return counts;
  }, [hourSlots, days, hourlyGrid]);

  return (
    <div
      className={
        flush
          ? 'h-full min-h-0 flex-1 overflow-auto'
          : 'min-h-0 flex-1 overflow-auto rounded-lg border border-slate-200 dark:border-slate-800'
      }
    >
      <table className="w-max min-w-full border-collapse text-[11px]">
        <thead className="sticky top-0 z-20">
          <tr className="bg-slate-50 dark:bg-slate-900">
            <th className="sticky left-0 z-30 min-w-[56px] border-b border-r border-slate-200 bg-slate-50 px-2 py-1.5 text-left text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">
              Hora
            </th>
            {days.map((day) => (
              <HourDayHeader key={day.iso} day={day} today={today} />
            ))}
          </tr>
        </thead>
        <tbody>
          {hourSlots.map((slot) => {
            const cardCount = maxCardsPerHour.get(slot.hour) ?? 1;
            const rowMinH = Math.max(28, cardCount * 26 + (cardCount > 0 ? 4 : 0));
            return (
              <tr key={slot.hour} className="group/row">
                <th
                  scope="row"
                  style={{ minHeight: rowMinH }}
                  className="sticky left-0 z-10 border-b border-r border-slate-200 bg-white px-2 py-1 text-left align-top font-normal tabular-nums text-[11px] font-semibold text-slate-600 transition-colors group-hover/row:bg-slate-50 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300 dark:group-hover/row:bg-slate-900"
                >
                  {slot.label}
                </th>
                {days.map((day) => {
                  const cell = hourlyGrid.get(`${day.iso}__${slot.hour}`)!;
                  const hasAlert = hourBlockHasUpcomingAlert(cell, today, nowMin);
                  return (
                    <HourCell
                      key={day.iso}
                      cell={cell}
                      isWeekend={day.isWeekend}
                      isToday={day.iso === today}
                      hasAlert={hasAlert}
                      minHeight={rowMinH}
                      onActivityClick={(a) =>
                        onCellClick(a.template, cell.date, a.execucao, a.code)
                      }
                    />
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const HOUR_CARD_STYLE: Record<
  HourActivityCardState,
  { bg: string; text: string; icon: React.ReactNode }
> = {
  ok: {
    bg: 'bg-emerald-50 border-emerald-200/80 hover:bg-emerald-100/80 dark:bg-emerald-900/30 dark:border-emerald-800 dark:hover:bg-emerald-900/45',
    text: 'text-emerald-800 dark:text-emerald-200',
    icon: <KamishibaiSymbolBadge symbol="O" size="sm" />,
  },
  desvio: {
    bg: 'bg-amber-50 border-amber-200/80 hover:bg-amber-100/80 dark:bg-amber-900/30 dark:border-amber-800 dark:hover:bg-amber-900/45',
    text: 'text-amber-800 dark:text-amber-200',
    icon: <KamishibaiSymbolBadge symbol="triangle" size="sm" />,
  },
  perdido: {
    bg: 'bg-slate-100 border-slate-200/80 hover:bg-slate-200/80 dark:bg-slate-800/40 dark:border-slate-700 dark:hover:bg-slate-800/60',
    text: 'text-slate-700 dark:text-slate-300',
    icon: <KamishibaiSymbolBadge symbol="X" size="sm" />,
  },
  futuro: {
    bg: 'bg-slate-50 border-slate-200/90 hover:bg-slate-100/80 dark:bg-slate-800/40 dark:border-slate-700 dark:hover:bg-slate-800/60',
    text: 'text-slate-600 dark:text-slate-300',
    icon: <span className="block h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400 dark:bg-slate-500" aria-hidden />,
  },
};

const ActivityHourCard: React.FC<{
  activity: HourCellActivity;
  hasAlert: boolean;
  onClick: () => void;
}> = ({ activity, hasAlert, onClick }) => {
  const style = HOUR_CARD_STYLE[activity.cardState];

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      title={`${activity.template.title} · ${formatTimeHHmm(activity.template.scheduled_time) ?? ''}`}
      className={`flex w-full max-w-full min-w-0 items-center gap-1 overflow-hidden rounded border px-1 py-0.5 text-left transition-shadow hover:shadow-sm focus-visible:ring-2 focus-visible:ring-blue-400 ${style.bg} ${
        hasAlert ? 'ring-1 ring-amber-400/80 ring-inset' : ''
      }`}
    >
      {style.icon}
      <span className={`block min-w-0 flex-1 truncate text-[9px] font-semibold leading-tight ${style.text}`}>
        <span className="tabular-nums">{activity.code}</span>
        <span className="mx-0.5 opacity-60">·</span>
        <span>{shortenActivityTitle(activity.template.title)}</span>
      </span>
      {hasAlert && <Bell size={8} className="shrink-0 text-amber-500" />}
    </button>
  );
};

const HourCell: React.FC<{
  cell: HourCellData;
  isWeekend: boolean;
  isToday: boolean;
  hasAlert: boolean;
  minHeight: number;
  onActivityClick: (activity: HourCellActivity) => void;
}> = ({ cell, isWeekend, isToday, hasAlert, minHeight, onActivityClick }) => {
  const hasActivities = cell.activities.length > 0;
  const bg = hasActivities
    ? ''
    : isToday
      ? 'bg-blue-50/30 dark:bg-blue-900/10'
      : isWeekend
        ? 'bg-slate-50 dark:bg-slate-800/40'
        : 'bg-white dark:bg-slate-950';

  return (
    <td
      className={`min-w-[148px] max-w-[168px] border-b border-l border-slate-100 align-top p-0.5 dark:border-slate-800/60 ${bg}`}
      style={{ minHeight }}
    >
      {hasActivities ? (
        <div className="flex w-full min-w-0 flex-col gap-0.5 p-0.5">
          {cell.activities.map((a) => (
            <ActivityHourCard
              key={a.template.id}
              activity={a}
              hasAlert={hasAlert}
              onClick={() => onActivityClick(a)}
            />
          ))}
        </div>
      ) : null}
    </td>
  );
};

const HourDayHeader: React.FC<{
  day: { iso: string; num: number; weekday: string; isWeekend: boolean };
  today: string;
}> = ({ day, today }) => (
  <th
    className={`min-w-[148px] border-b border-slate-200 px-1 py-1 text-center font-semibold dark:border-slate-800 ${
      day.iso === today
        ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
        : day.isWeekend
          ? 'bg-slate-50 text-slate-500 dark:bg-slate-800/50'
          : 'text-slate-600 dark:text-slate-300'
    }`}
  >
    <div className="text-[11px] leading-none tabular-nums">{day.num}</div>
    <div className="mt-0.5 text-[8px] font-medium uppercase tracking-tight text-slate-400">{day.weekday}</div>
  </th>
);

// ─── Subcomponentes compartilhados ──────────────────────────

const DayHeader: React.FC<{
  day: { iso: string; num: number; weekday: string; isWeekend: boolean };
  today: string;
}> = ({ day, today }) => (
  <th
    className={`w-9 border-b border-slate-200 px-0 py-1 text-center font-semibold dark:border-slate-800 ${
      day.iso === today
        ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
        : day.isWeekend
          ? 'bg-slate-50 text-slate-500 dark:bg-slate-800/50'
          : 'text-slate-600 dark:text-slate-300'
    }`}
  >
    <div className="text-[11px] leading-none tabular-nums">{day.num}</div>
    <div className="mt-0.5 text-[8px] font-medium uppercase tracking-tight text-slate-400">{day.weekday}</div>
  </th>
);

const InactiveDayCell: React.FC<{ isWeekend: boolean; isToday: boolean }> = () => (
  <td className={`border-b border-l border-slate-100 p-0 dark:border-slate-800/60 ${KAMISHIBAI_NA_CELL_BG}`}>
    <div className="h-8 w-9" aria-hidden />
  </td>
);

const ActivityCell: React.FC<{
  status: LstStatus | 'pendente' | null;
  isWeekend: boolean;
  isToday: boolean;
  label: string;
  onClick: () => void;
}> = ({ status, isWeekend, isToday, label, onClick }) => (
  <td className="border-b border-l border-slate-100 p-0 dark:border-slate-800/60">
    <KamishibaiMatrixCell
      status={status}
      onClick={onClick}
      isToday={isToday}
      isWeekend={isWeekend}
      label={label}
    />
  </td>
);

const ViewToggleButton: React.FC<{ active: boolean; onClick: () => void; label: string }> = ({
  active,
  onClick,
  label,
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`rounded-[5px] px-3 py-1.5 text-[11px] font-semibold transition-all duration-150 ${
      active
        ? 'bg-white text-slate-800 shadow-sm dark:bg-slate-800 dark:text-slate-100'
        : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
    }`}
  >
    {label}
  </button>
);

const EmptyState: React.FC<{ cargo: LstCargo; flush?: boolean }> = ({ cargo, flush = false }) => (
  <div
    className={
      flush
        ? 'flex h-full min-h-0 flex-1 items-center justify-center text-[12px] text-slate-400'
        : 'flex min-h-0 flex-1 items-center justify-center rounded-lg border border-slate-200 text-[12px] text-slate-400 dark:border-slate-800'
    }
  >
    Nenhuma rotina padrão cadastrada para {LST_CARGO_LABELS[cargo]}.
  </div>
);

const SummaryPill: React.FC<{
  label: string;
  value: number;
  tone: 'emerald' | 'amber' | 'red' | 'blue';
}> = ({ label, value, tone }) => {
  const cls =
    tone === 'emerald'
      ? 'text-emerald-600 dark:text-emerald-400'
      : tone === 'amber'
        ? 'text-amber-600 dark:text-amber-400'
        : tone === 'blue'
          ? 'text-blue-600 dark:text-blue-400'
          : 'text-red-600 dark:text-red-400';
  return (
    <div className="flex flex-col items-end">
      <span className={`text-[16px] font-bold tabular-nums ${cls}`}>{value}</span>
      <span className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">{label}</span>
    </div>
  );
};

const LegendItem: React.FC<{ icon: React.ReactNode; label: string }> = ({ icon, label }) => (
  <span className="inline-flex items-center gap-1">
    {icon}
    {label}
  </span>
);

export default LstMatrixMensal;
